<template>
    <div class="w-full absolute top-0 flex items-center flex-col">
        <image-with-text-overlay :content="{image: content.data['hero-image'], sticky: false, priority: true}" />
<!--        <div class="relative w-full h-auto max-h-screen max-w-hd flex flex-col gap-2"><img src="/api/files/image/6a1d769ff1a5bc7654709d8e" srcset="/api/files/image/6a1d769ff1a5bc7654709d8e?size=128x128 128w, /api/files/image/6a1d769ff1a5bc7654709d8e?size=400x400 400w, /api/files/image/6a1d769ff1a5bc7654709d8e?size=800x800 800w, /api/files/image/6a1d769ff1a5bc7654709d8e?size=1200x1200 1200w, /api/files/image/6a1d769ff1a5bc7654709d8e?size=1920x1920 1920w" sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 100vw" class="inset-0 w-full h-full object-cover max-h-screen">&lt;!&ndash;&ndash;&gt;</div>-->
        <article class="w-full pt-34 mb-28">
            <div class="container grid grid-cols-3 md:grid-cols-12 gap-10">
                <div class="flex gap-2 text-dark-blue/50 md:col-start-2 lg:!col-start-3 col-span-full md:col-span-10 lg:col-span-8 -mb-10">
                    <time
                        class="uppercase"
                        :datetime="publicationDate ?? undefined"
                    >{{ format_date(publicationDate ?? content._versioning.updated) }}</time>
                    <div>·</div>
                    <div class="uppercase ">READING TIME</div>
                    <div class="font-semibold">{{getLocalizedValue(content.data._computed.readingTime).minutes}} MIN</div>
                </div>
                <h2 class="view-h2 col-default-start col-span-full md:col-span-10 lg:col-span-8">{{ getLocalizedValue(content.data.title)}}</h2>
                <div class="first:col-start-1 md:col-start-2 lg:col-start-4 col-span-full">
                    <ContentReference
                        source-bucket="blog"
                        :source-data="content.data"
                        field-path="author"
                        v-slot="{ label, image }"
                    >
                        <div v-if="label" class="mt-auto flex items-center gap-2">
                            <img
                                v-if="image"
                                :src="`/api/files/image/${image}?size=thumb`"
                                :alt="label"
                                class="size-8 rounded-full object-cover"
                            />
                            <div>{{t('author-prefix')}} {{ label }}</div>
                        </div>
                    </ContentReference>

                </div>
                <div class="col-span-full grid grid-cols-subgrid gap-y-20 mt-16">
                    <component
                        v-for="component in component_list"
                        :is="component.component"
                        :content="component.content"
                        :properties="component.properties"
                        :items="component.items"
                        v-bind="component.props"
                    />
                </div>
            </div>
            <global-contact-banner
                v-if="content.data['enable-contact-banner']"
                class="mt-10"
                variant="simple"
                :title="t('contact-banner.title')"
                :text="content.data['contact-banner']"
            />
<!--            {{content}}-->
        </article>
        <Footer />
    </div>
</template>

<script lang="ts" setup>
import { Pages } from "#shared/types/Pages"
import { useRoute } from "vue-router"
import type { ApiResponse } from "#layers/cms/shared/types/ApiResponse"
import type { BucketData } from "#layers/cms/shared/types/bucketData"
import Quote from "~/components/view/Quote.vue"
import DuoParalaxImages from "~/components/view/DuoParalaxImages.vue"
import DuoParalaxMedia from "~/components/view/DuoParalaxMedia.vue"
import YoutubeEmbed from "~/components/UI/YoutubeEmbed.vue"
import VideoWithTextBlock from "~/components/view/VideoWithTextBlock.vue"
import BehindTheScenes from "~/components/view/BehindTheScenes.vue"
import LargeText from "~/components/view/LargeText.vue"
import ImageCarouselRegular from "~/components/view/ImageCarouselRegular.vue"
import MediaCarousel from "~/components/view/MediaCarousel.vue"
import MediaWithTextBlock from "~/components/view/MediaWithTextBlock.vue"
import MediaWithCaption from "~/components/view/MediaWithCaption.vue"
import ImageWithTextBlock from "~/components/view/ImageWithTextBlock.vue"
import VideoWithText from "~/components/view/VideoWithText.vue"
import TextBlock from '~/components/view/blog/TextBlock.vue'
import ImageWithTextOverlay from "~/components/view/ImageWithTextOverlay.vue"
import ImageWithCaption from '~/components/view/blog/ImageWithCaption.vue'
import { kebabCase } from 'lodash-es'
import ContactBannerMoreInfo from "~/components/view/ContactBannerMoreInfo.vue"
import ContactBanner from "~/components/view/ContactBanner.vue"
import GlobalContactBanner from "~/components/view/contact-banners/global-contact-banner.vue"
import Footer from "~/components/view/Footer.vue"
import BlockText2Columns from "~/components/view/BlockText2Columns.vue"
import type { Component } from 'vue'

const siteStore = useSiteStore()
const route = useRoute()
const { params: {slug} } = route
const config = useRuntimeConfig()
const {format_date} = useTextUtils()
const {get_value: getLocalizedValue} = useLocalizedValue()
const { t } = useI18n({useScope: 'local'})


definePageMeta({
    layout: 'view',
    pageTransition: {
        onBeforeEnter: (el) => {
            if (window) window.scroll({top: 0})
        }
    }
})


const { data: response, error: requestError } = await useFetch<ApiResponse<BucketData>>(
    `/api/bucketContent/blog/${ encodeURIComponent(String(slug)) }?by_slug=true`,
    { key: `website-blog-item-${ slug }` }
)

if (requestError.value) {
    throw createError({
        statusCode: requestError.value.statusCode ?? 500,
        statusMessage: `Insight content could not be loaded. slug:${slug}`,
        cause: requestError.value
    })
}

if (!response.value?.success) {
    throw createError({
        statusCode: 500,
        statusMessage: `Insight content could not be loaded. slug:${slug}`,
        cause: new Error(response.value?.message)
    })
}

const content = response.value.data
const publicationDate = typeof content.data.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(content.data.date)
    ? content.data.date
    : null
const title = getLocalizedValue<string>(content.data.seo?.title) ?? getLocalizedValue<string>(content.data.title) ?? 'Insight'
const description = getLocalizedValue<string>(content.data.seo?.description) ?? getLocalizedValue<string>(content.data.subtitle) ?? undefined
const imageId = content.data.seo?.image ?? content.data['hero-image']
const image = imageId
    ? `${config.public.publicHost}/api/files/image/${imageId}`
    : undefined
const pageUrl = `${config.public.publicHost}${route.path}`

useHead({
    title,
    script: publicationDate ? [{
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: title,
            description,
            image,
            datePublished: publicationDate,
            dateModified: content._versioning.updated,
            mainEntityOfPage: pageUrl,
            publisher: {
                '@type': 'Organization',
                name: 'YIPP',
                url: config.public.publicHost
            }
        }).replace(/</g, '\\u003c')
    }] : []
})

useSeoMeta({
    description,
    ogType: 'article',
    ogTitle: title,
    ogDescription: description,
    ogImage: image,
    ogUrl: pageUrl,
    articlePublishedTime: publicationDate ?? undefined,
    articleModifiedTime: String(content._versioning.updated)
})

onMounted(() => {
    siteStore.setCurrentPage(Pages.INSIGHTS)
})

const component_list: {
    component: Component,
    content: any,
    properties: object,
    items?: any[],
    props?: Record<string, unknown>
}[] = []

const create_components = () => {
    const keys = Object.keys(content.data.content) as Array<keyof typeof content.data.content>
    keys.forEach(component_key => {
        const component_type = kebabCase(content.data.content[component_key]["component-type"])

        if (component_type === 'quote') {
            component_list.push({
                component: Quote,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'block-text' || component_type === 'blocktext') {
            component_list.push({
                component: TextBlock,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'quote-with-image') {
            component_list.push({
                component: Quote,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'image') {
            component_list.push({
                component: ImageWithCaption,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'video-with-text') {
            component_list.push({
                component: VideoWithText,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                props: {
                    containerless: true
                }
            })
        } else if (component_type === 'image-with-text-block') {
            component_list.push({
                component: ImageWithTextBlock,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'block-text-2-columns') {
            component_list.push({
                component: BlockText2Columns,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                props: {
                    class: 'col-span-full'
                }
            })
        } else if (component_type === 'duo-paralax-images') {
            component_list.push({
                component: DuoParalaxImages,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                props: {
                    containerless: true
                }
            })
        } else if (component_type === 'duo-paralax-media') {
            component_list.push({
                component: DuoParalaxMedia,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                props: {
                    containerless: true
                }
            })
        } else if (component_type === 'image-carousel') {
            component_list.push({
                component: ImageCarouselRegular,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.images
            })
        } else if (component_type === 'media-carousel') {
            component_list.push({
                component: MediaCarousel,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.items
            })
        } else if (component_type === 'media-with-text-block') {
            component_list.push({
                component: MediaWithTextBlock,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties
            })
        } else if (component_type === 'media-with-caption') {
            component_list.push({
                component: MediaWithCaption,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                props: {
                    containerless: true
                }
            })
        } else if (component_type === 'large-text') {
            component_list.push({
                component: LargeText,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.images,
                props: {
                    containerless: true
                }
            })
        } else if (component_type === 'behind-the-scenes') {
            component_list.push({
                component: BehindTheScenes,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
                items: content.data.content[component_key].content.items
            })
        } else if (component_type === 'video-with-text-block') {
            component_list.push({
                component: VideoWithTextBlock,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
            })
        } else if (component_type === 'youtube-video-with-text-block') {
            component_list.push({
                component: YoutubeEmbed,
                content: content.data.content[component_key].content,
                properties: content.data.content[component_key].properties,
            })
        }
    })
}

create_components()

</script>

<i18n lang="yaml">
en:
    author-prefix: By
    contact-banner:
        title: Want to know more?
        contact: Contact us
nl:
    author-prefix: Door
    contact-banner:
        title: Meer weten?
        contact: Neem contact op
</i18n>

<style scoped>
.col-default-start {
    grid-column-start: 1;
}

@media (width >= 768px) {
    .col-default-start {
        grid-column-start: 2;
    }
}

@media (width >= 1024px) {
    .col-default-start {
        grid-column-start: 3;
    }
}
</style>
